import { EMAIL_REGEX, LETTERS_OR_DIGITS_WITHOUT_UPPER_LETTERS } from "@finbackoffice/fe-core";
import { object, string } from "yup";

export const userInfoValidationSchema = object().shape({
    username: string()
        .when("$configObj", ([config], schema) => {
            if (config?.username.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        })
        .when("$configObj", ([config], schema) => {
            return schema
                .min(
                    config.username.min_length,
                    config.t("validation_err_minMaxLength", {
                        min_length: config.username.min_length,
                        max_length: config.username.max_length,
                    }),
                )
                .max(
                    config.username.max_length,
                    config.t("validation_err_minMaxLength", {
                        min_length: config.username.min_length,
                        max_length: config.username.max_length,
                    }),
                );
        })
        .when("$configObj", ([config], schema) => {
            return schema.matches(/^(?![0-9]*$)/, config.t("validation_err_notOnlyNum"));
        })
        .when("$configObj", ([config], schema) => {
            return schema.matches(
                config.username.dynamic_regexp ?? LETTERS_OR_DIGITS_WITHOUT_UPPER_LETTERS,
                config.t("validation_err_letersOrDigits"),
            );
        }),
    phone: string()
        .trim()
        .when("$configObj", ([config], schema) => {
            if (config.phone.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        }),
    email: string()
        .when("$configObj", ([config], schema) => {
            if (config.email.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        })
        .when("$configObj", ([config], schema) => {
            return schema.matches(EMAIL_REGEX, config.t("validation_err_notValid"));
        })
        .when("$configObj", ([config], schema) => {
            if (config.email.max_length) {
                return schema.max(
                    config.email.max_length,
                    config.t("validation_err_maxLength", {
                        max_length: config.email.max_length,
                    }),
                );
            }
            return schema;
        }),
    first_name: string()
        .when("$configObj", ([config], schema) => {
            if (config.first_name.max_length) {
                return schema.max(
                    config.first_name.max_length,
                    config.t("validation_err_maxLength", {
                        max_length: config.first_name.max_length,
                    }),
                );
            }
            return schema;
        })
        .when("$configObj", ([config], schema) => {
            if (config.first_name.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        }),
    last_name: string()
        .when("$configObj", ([config], schema) => {
            if (config.last_name.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        })
        .when("$configObj", ([config], schema) => {
            if (config.last_name.max_length) {
                return schema.max(
                    config.last_name.max_length,
                    config.t("validation_err_maxLength", {
                        max_length: config.last_name.max_length,
                    }),
                );
            }

            return schema;
        }),
    birth_date: string().when("$configObj", ([config], schema) => {
        if (config.birth_date.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
    referral: string()
        .when("$configObj", ([config], schema) => {
            if (config.referral.required) {
                return schema.required(config.t("validation_err_required"));
            }
            return schema.notRequired().transform((value: string) => (value ? value : null));
        })
        .when("$configObj", ([config], schema) => {
            return schema.matches(
                config.referral.dynamic_regexp ?? LETTERS_OR_DIGITS_WITHOUT_UPPER_LETTERS,
                {
                    excludeEmptyString: true,
                    message: config.t("validation_err_letersOrDigits"),
                },
            );
        })
        .when("$configObj", ([config], schema) => {
            if (config.referral.min_length && config.referral.max_length) {
                return schema
                    .min(
                        config.referral.min_length,
                        config.t("validation_err_minMaxLength", {
                            min_length: config.referral.min_length,
                            max_length: config.referral.max_length,
                        }),
                    )
                    .max(
                        config.referral.max_length,
                        config.t("validation_err_minMaxLength", {
                            min_length: config.referral.min_length,
                            max_length: config.referral.max_length,
                        }),
                    );
            }
            return schema;
        }),
    country: string().when("$configObj", ([config], schema) => {
        if (config.country.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
    state: string().when("$configObj", ([config], schema) => {
        if (config.state.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
    gender: string().when("$configObj", ([config], schema) => {
        if (config.gender.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
    city: string().when("$configObj", ([config], schema) => {
        if (config.city.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
    address: string().when("$configObj", ([config], schema) => {
        if (config.address.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
    zip_code: string().when("$configObj", ([config], schema) => {
        if (config.zip_code.required) {
            return schema.required(config.t("validation_err_required"));
        }
        return schema.notRequired().transform((value: string) => (value ? value : null));
    }),
});
